import { z } from 'zod';

/**
 * Display-name rules, shared by registration, guest play and the WebSocket `hello`.
 *
 * Letters, digits, spaces, `_` and `-` only: names are drawn above a player's blob at small
 * sizes, and anything wider (emoji, combining marks, RTL overrides) either breaks the layout or
 * is used to impersonate other players.
 */
export const DISPLAY_NAME_MIN_LENGTH = 3;
export const DISPLAY_NAME_MAX_LENGTH = 16;
export const DISPLAY_NAME_PATTERN = /^[A-Za-z0-9_ -]+$/;

export const displayNameSchema = z
  .string()
  .trim()
  .min(DISPLAY_NAME_MIN_LENGTH, `Name must be at least ${DISPLAY_NAME_MIN_LENGTH} characters`)
  .max(DISPLAY_NAME_MAX_LENGTH, `Name must be at most ${DISPLAY_NAME_MAX_LENGTH} characters`)
  .regex(DISPLAY_NAME_PATTERN, 'Name may only contain letters, numbers, spaces, _ and -');

/**
 * THE GUEST NAMESPACE IS RESERVED FOR GUESTS.
 *
 * A guest never chooses a name: the server mints one (`generateGuestName` in
 * `services/auth/auth.service.ts`) as this prefix plus hex, and that is the only name a guest
 * ever has. The prefix is here rather than beside the generator because the *reservation* is
 * the shared half — three surfaces have to refuse it (register, the rename on `/profile`, and
 * the name a Google profile is squeezed into), and a predicate any one of them re-implemented
 * would be the one that let somebody register as `Chomp-deadbeef` and read as a guest.
 *
 * It matches the generated *shape* — the prefix followed by hex digits and nothing else —
 * rather than the prefix alone, because the point is only that a registered name cannot be
 * mistaken for a minted one. `Chomp-Master` is somebody's name and stays available; the
 * suffix length is not pinned, so changing the generator's entropy cannot open a hole here.
 */
export const GUEST_NAME_PREFIX = 'Chomp-';
const GUEST_NAME_SHAPE = /^chomp-[0-9a-f]+$/i;

export function isReservedGuestName(name: string): boolean {
  return GUEST_NAME_SHAPE.test(name.trim());
}

/**
 * A name a *registered* account may hold: `displayNameSchema` plus the reservation above.
 *
 * Registration has always required a name and the column has always been case-insensitively
 * unique (`users_display_name_lower_unique`); what this adds is the one thing a registered
 * player must not be able to call themselves. Guests keep the plain `displayNameSchema` —
 * their minted name has to parse under it, and it would fail this one by construction.
 */
export const registeredDisplayNameSchema = displayNameSchema.refine(
  (name) => !isReservedGuestName(name),
  'That name is reserved for guest players. Pick another one.',
);

export const emailSchema = z.string().trim().toLowerCase().email('Invalid email address');

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters');

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: registeredDisplayNameSchema,
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

/**
 * Guest play: no account, and DELIBERATELY NO NAME.
 *
 * A guest is handed a minted `Chomp-xxxxxxxx` and cannot ask for anything else. The reason is
 * that `display_name` is one case-insensitively unique namespace shared by guests and accounts:
 * a guest who could type a name would be squatting one a real registration might want, at no
 * cost at all, since a guest row is reclaimed by a device id anybody can send. Choosing a name
 * is what registering buys.
 *
 * The field is gone from the schema rather than parsed and ignored, so there is no way to send
 * one that quietly does nothing. NO PROTOCOL BUMP: this is the tRPC input for `auth.guest`, and
 * the name on the wire — `clientHelloSchema.name`, `joinRequestSchema.displayName` — is the one
 * the account already holds and is unchanged.
 *
 * `deviceId` is an opaque client-generated id used to keep a guest's stats across a page
 * reload — never treat it as proof of identity.
 */
export const guestSchema = z.object({
  deviceId: z.string().trim().min(8).max(64).optional(),
});

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type GuestInput = z.infer<typeof guestSchema>;

/**
 * Native Google sign-in: the assertion the OS account picker handed the shell.
 *
 * The bundled Capacitor build cannot use the redirect dance at all — Google refuses OAuth inside
 * an embedded WebView (`disallowed_useragent`) — so the shell asks the OS for an `id_token` and
 * posts that here instead of following a 302. The browser's path is untouched and does not use
 * this schema.
 *
 * ONE FIELD, AND DELIBERATELY NOTHING ELSE. No client id (the token is checked against the
 * server's own `GOOGLE_CLIENT_ID`), no account, no scopes: every one of those is a thing a
 * client that could name it could name *wrongly*, and the whole point of this endpoint is that
 * the caller's only contribution is a blob Google signed.
 *
 * The length bound is generous rather than tight. A Google `id_token` is around a kilobyte today
 * and its size is Google's to change; what the bound is for is refusing a body this server would
 * otherwise parse before rejecting.
 */
export const GOOGLE_ID_TOKEN_MAX_LENGTH = 8192;

export const googleNativeSignInSchema = z.object({
  idToken: z
    .string()
    .trim()
    .min(1, 'Missing Google identity token')
    .max(GOOGLE_ID_TOKEN_MAX_LENGTH, 'Google identity token is implausibly long'),
});

export type GoogleNativeSignInInput = z.infer<typeof googleNativeSignInSchema>;

/**
 * Confirming an address: one opaque token out of a link, and deliberately nothing else.
 *
 * No email beside it and no user id. The token is the whole credential — it is unguessable, it
 * is single-use, and it names the account by itself — so a second field would be either
 * redundant or a way for a caller to ask for a different account than the one they hold a link
 * to. `googleNativeSignInSchema`'s rule, one feature over.
 *
 * The alphabet is base64url because that is what `randomBytes(...).toString('base64url')`
 * produces, and the bounds are generous rather than exact: what they are for is refusing a body
 * this server would otherwise hash before finding nothing.
 */
export const EMAIL_VERIFICATION_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

export const emailVerificationTokenSchema = z
  .string()
  .trim()
  .min(16, 'That confirmation link is incomplete')
  .max(128, 'That confirmation link is not one of ours')
  .regex(EMAIL_VERIFICATION_TOKEN_PATTERN, 'That confirmation link is not one of ours');

export const verifyEmailSchema = z.object({ token: emailVerificationTokenSchema });

/**
 * Asking for another confirmation mail.
 *
 * It takes an ADDRESS rather than reading the session, because the screen that needs it most is
 * the one where there is no session: a login refused for an unconfirmed address. The procedure
 * answers the same way whatever the address turns out to be — see `auth.resendVerification` —
 * so this input is not an oracle for who has an account here.
 */
export const resendVerificationSchema = z.object({ email: emailSchema });

export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;
